import React, { useCallback, useEffect, useState } from 'react';
import API from '../../services/api';
import QuestionText from '../../components/ui/QuestionText';
import PaginationControls from '../../components/ui/PaginationControls';
import './Analytics.css';

const TABS = [['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected']];
const PAGE_SIZE = 20;

// Super admin: colleges offer questions to the platform bank; approving copies one into the platform bank
// (the college keeps its own), rejecting sends a note back.
export default function QuestionReview() {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});
  const [rowErr, setRowErr] = useState({});
  // The server has always paginated this queue (audit SA-08) — the client just never asked for page 2.
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setErr(''); setData(null);
    API.get('/questions/review', { params: { status, page, limit: PAGE_SIZE } })
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.message || 'We could not load the review queue.'));
  }, [status, page]);
  useEffect(() => { setPage(1); }, [status]);
  useEffect(load, [load]);

  const decide = async (q, decision) => {
    setBusy(q.id); setRowErr(p => ({ ...p, [q.id]: '' }));
    try {
      await API.post(`/questions/${q.id}/review`, { decision, note: notes[q.id] || '' });
      load();
    } catch (e) { setRowErr(p => ({ ...p, [q.id]: e.response?.data?.message || 'Could not save that decision.' })); }
    finally { setBusy(null); }
  };

  return (
    <div className="axh-page">
      <div className="axh-topbar">
        <div><h1>Question Review</h1><p>Questions colleges have offered to the platform bank.</p></div>
      </div>

      <div role="tablist" aria-label="Review status" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {TABS.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={status === k} className={`axh-btn${status === k ? ' primary' : ''}`} onClick={() => setStatus(k)}>{l}</button>
        ))}
      </div>

      {err && <div className="axh-error" role="alert">{err} <button type="button" className="axh-btn" onClick={load}>Retry</button></div>}
      {!err && !data && <div className="axh-loading"><div className="axh-spinner" />Loading…</div>}
      {data && data.data.length === 0 && <div className="axh-card"><div className="axh-empty">{status === 'pending' ? "You're all caught up — nothing is waiting for review." : `No ${status} questions.`}</div></div>}
      {data && data.data.map(q => (
        <div className="axh-card" key={q.id} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12, color: 'var(--z-muted)', marginBottom: 8 }}>
            <span>{q.college_name}</span><span>· {q.topic_name || 'No topic'}</span><span>· {q.difficulty}</span>{q.submitted_by && <span>· by {q.submitted_by}</span>}
          </div>
          <div style={{ marginBottom: 8 }}><QuestionText text={q.question_text} /></div>
          <ol style={{ margin: '0 0 8px', paddingLeft: 20, fontSize: 14 }}>
            {(q.options || []).map((o, i) => {
              const t = typeof o === 'string' ? o : (o.option_text || String(o));
              return <li key={i} style={t === q.correct_answer ? { color: 'var(--z-success)', fontWeight: 700 } : undefined}>{t}</li>;
            })}
          </ol>
          {q.explanation && <p style={{ fontSize: 13, color: 'var(--z-muted)' }}>{q.explanation}</p>}
          {q.review_note && status !== 'pending' && <p style={{ fontSize: 13 }}><strong>Note:</strong> {q.review_note}</p>}
          {status === 'pending' && (
            <>
              <label className="ann-field" style={{ display: 'block', margin: '8px 0' }}>
                <span style={{ fontSize: 12 }}>Note to the college (required when rejecting)</span>
                <input value={notes[q.id] || ''} onChange={e => setNotes(p => ({ ...p, [q.id]: e.target.value }))} maxLength={1000} style={{ width: '100%' }} />
              </label>
              {rowErr[q.id] && <div className="axh-error" role="alert">{rowErr[q.id]}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="axh-btn primary" disabled={busy === q.id} onClick={() => decide(q, 'approve')}>Approve to platform bank</button>
                <button type="button" className="axh-btn" style={{ color: 'var(--z-danger)' }} disabled={busy === q.id} onClick={() => decide(q, 'reject')}>Reject</button>
              </div>
            </>
          )}
        </div>
      ))}
      {data && data.data.length > 0 && <PaginationControls page={page} totalPages={data.metadata.total_pages} total={data.metadata.total} onChange={setPage} />}
    </div>
  );
}
